import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Loader2, ArrowLeft, Quote, Lightbulb, AlertTriangle, ListChecks } from 'lucide-react';
import MotoShell from '../components/MotoShell';
import { api } from '../lib/api';

const CALLOUT_TONES = {
  warning: 'border-amber-500/30 bg-amber-500/[0.06] text-amber-300',
  danger: 'border-red-500/30 bg-red-500/[0.06] text-red-300',
  info: 'border-sky-500/30 bg-sky-500/[0.06] text-sky-300',
  success: 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300',
};

export default function MotoSatellitePage() {
  const { parentSlug, satelliteSlug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setDoc(null); setErr(false);
    api.motoSatellite(parentSlug, satelliteSlug).then((d) => {
      setDoc(d);
      document.title = `${d.title} | Code 2 Roues`;
      window.scrollTo(0, 0);
    }).catch(() => setErr(true));
  }, [parentSlug, satelliteSlug]);

  if (err) return <MotoShell><div className="max-w-3xl mx-auto px-4 py-24 text-center text-white/60">Article introuvable. <Link to={`/moto/apprendre/${parentSlug}`} className="text-sky-400">← Retour au dossier</Link></div></MotoShell>;
  if (!doc) return <MotoShell><div className="py-32 flex justify-center"><Loader2 className="animate-spin text-sky-400" size={28} /></div></MotoShell>;

  return (
    <MotoShell>
      <article className="max-w-3xl mx-auto px-4 lg:px-6 pt-8 sm:pt-12" data-testid={`moto-satellite-${doc.slug}`}>
        <Link to={`/moto/apprendre/${parentSlug}`} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-[12.5px] transition-colors" data-testid="moto-sat-back">
          <ArrowLeft size={13} /> Dossier : {doc.parentTitle || parentSlug}
        </Link>
        <div className="mt-5 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-[0.2em] font-bold">{doc.kicker}</div>
        <h1 className="font-serif-editorial text-[26px] sm:text-[38px] leading-[1.1] mt-2">{doc.title}</h1>
        {doc.teaser && <p className="text-white/60 text-[14px] mt-3 leading-relaxed">{doc.teaser}</p>}
        {doc.readTimeMin && <div className="mt-3 text-white/40 text-[11.5px] font-mono-spec inline-flex items-center gap-1.5"><Clock size={12} /> {doc.readTimeMin} min</div>}

        <div className="mt-8 space-y-5 mb-8">
          {(doc.body || []).map((b, i) => {
            if (b.type === 'subtitle') return <h2 key={i} className="font-serif-editorial text-white text-[21px] sm:text-[25px] leading-tight pt-2">{b.text}</h2>;
            if (b.type === 'paragraph') return <p key={i} className="text-white/75 text-[14px] leading-[1.75]" dangerouslySetInnerHTML={{ __html: b.text }} />;
            if (b.type === 'keypoints') {
              return (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5 mb-2"><ListChecks size={12} /> {b.title || 'À retenir'}</div>
                  <ul className="space-y-1.5">
                    {(b.items || []).map((it, j) => <li key={j} className="flex gap-2 text-[13px] text-white/80"><span className="text-sky-400 shrink-0">▸</span><span dangerouslySetInnerHTML={{ __html: it }} /></li>)}
                  </ul>
                </div>
              );
            }
            if (b.type === 'callout') {
              const tone = CALLOUT_TONES[b.tone] || CALLOUT_TONES.info;
              const Icon = b.tone === 'warning' || b.tone === 'danger' ? AlertTriangle : Lightbulb;
              return (
                <aside key={i} className={`rounded-xl border p-4 ${tone.split(' ').slice(0, 2).join(' ')}`}>
                  {b.title && <div className={`text-[10.5px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5 mb-1.5 ${tone.split(' ')[2]}`}><Icon size={12} /> {b.title}</div>}
                  <div className="text-white/75 text-[13.5px] leading-relaxed" dangerouslySetInnerHTML={{ __html: b.text }} />
                </aside>
              );
            }
            if (b.type === 'quote') {
              return (
                <blockquote key={i} className="border-l-2 border-sky-500 pl-4 py-1">
                  <Quote size={16} className="text-sky-400 mb-1.5" />
                  <p className="font-serif-editorial text-white/90 text-[17px] leading-relaxed italic">{b.text}</p>
                  {b.source && <cite className="block text-white/45 text-[12px] mt-2 not-italic">— {b.source}</cite>}
                </blockquote>
              );
            }
            if (b.type === 'sources') {
              return (
                <div key={i} className="pt-4 border-t border-white/10">
                  <div className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-2">Sources</div>
                  <ul className="space-y-1">
                    {(b.items || []).map((it, j) => <li key={j} className="text-white/45 text-[12px]" dangerouslySetInnerHTML={{ __html: it }} />)}
                  </ul>
                </div>
              );
            }
            return null;
          })}
        </div>
      </article>
    </MotoShell>
  );
}
